import { type FC, useId } from "react";

import { SIGNAL_HEX } from "./colorTokens";

interface SparklineProps {
  values: number[];
  /** Hex color or CSS color string. Defaults to emerald-500. */
  color?: string;
  width?: number;
  height?: number;
  /** Render a filled area below the line. Defaults to true. */
  fill?: boolean;
  /** Optional class for the wrapping svg. */
  className?: string;
}

/**
 * Minimal SVG sparkline — area + line, no axes, no deps.
 * Empty/flat series render as a dim baseline so the card doesn't twitch.
 */
export const Sparkline: FC<SparklineProps> = ({
  values,
  color = SIGNAL_HEX.healthy,
  width = 80,
  height = 20,
  fill = true,
  className,
}) => {
  const gradientId = useId();

  if (values.length < 2) {
    return (
      <svg width={width} height={height} className={className}>
        <line
          x1={0}
          y1={height - 0.5}
          x2={width}
          y2={height - 0.5}
          stroke={color}
          strokeOpacity={0.25}
          strokeWidth={1}
        />
      </svg>
    );
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return { x, y };
  });

  const linePath = points
    .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(" ");
  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg width={width} height={height} className={className}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      {fill && <path d={areaPath} fill={`url(#${gradientId})`} />}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={1.25}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
